import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import {
  PLATAFORMAS,
  carregarTrafegoPago,
  nomePlataforma,
  type DadosTrafego,
  type LinhaCampanha,
  type Metricas,
  type Plataforma,
} from '../api/trafegoPago'
import { carregarConversoesPrincipais, type ConversaoPrincipal } from '../api/conversoes'
import { AvisoConversao } from '../components/AvisoConversao'
import { ChartEmpty } from '../components/ChartEmpty'
import { ColunasPorPlataforma, type PontoDiario } from '../components/charts/ColunasPorPlataforma'
import { useCoresPlataforma } from '../components/charts/coresPlataforma'
import { TabelaDiaria } from '../components/charts/TabelaDiaria'
import { KpiCard } from '../components/KpiCard'
import { AnaliseCampanhas } from '../components/AnaliseCampanhas'
import { DetalheAnuncios } from '../components/DetalheAnuncios'
import { DiagnosticoCusto } from '../components/DiagnosticoCusto'
import { LeituraIA } from '../components/LeituraIA'
import { PainelShell } from '../components/PainelShell'
import { useFiltros } from '../state/FiltrosContext'
import {
  formatarConversoes,
  formatarInteiro,
  formatarNumero,
  formatarPercentual,
  formatarReais,
  formatarReaisCompacto,
  formatarReaisEixo,
  formatarRoas,
  razao,
} from '../utils/formato'
import { diasDoIntervalo } from '../utils/periodo'

const ZERO: Metricas = { investimento: 0, impressoes: 0, cliques: 0, conversoes: 0, valorConversoes: 0 }

const somar = (a: Metricas, b: Metricas): Metricas => ({
  investimento: a.investimento + b.investimento,
  impressoes: a.impressoes + b.impressoes,
  cliques: a.cliques + b.cliques,
  conversoes: a.conversoes + b.conversoes,
  valorConversoes: a.valorConversoes + b.valorConversoes,
})

/** Variação percentual; null quando não há base (período anterior zerado ou sem valor). */
const variacao = (atual: number | null, anterior: number | null) =>
  atual == null || anterior == null || anterior === 0 ? null : ((atual - anterior) / Math.abs(anterior)) * 100

const traco = (v: number | null, f: (n: number) => string) => (v == null ? '—' : f(v))

export function TrafegoPagoPage() {
  const { intervalo, marcaId, marcas } = useFiltros()
  const [plataforma, setPlataforma] = useState<Plataforma | ''>('')
  // A campanha escolhida só vale para a marca e a plataforma em que foi escolhida.
  const [campanha, setCampanha] = useState<{ id: string; marcaId: string; plataforma: string } | null>(null)
  const campanhaId =
    campanha && campanha.marcaId === marcaId && campanha.plataforma === plataforma ? campanha.id : ''
  const [modo, setModo] = useState<'grafico' | 'tabela'>('grafico')
  const [dados, setDados] = useState<DadosTrafego | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [principais, setPrincipais] = useState<ConversaoPrincipal[]>([])

  useEffect(() => {
    carregarConversoesPrincipais().then(setPrincipais).catch(() => setPrincipais([]))
  }, [])

  const filtro = useMemo(
    () => ({ intervalo, marcaId, plataforma, campanhaId }),
    [intervalo, marcaId, plataforma, campanhaId],
  )

  useEffect(() => {
    let cancelado = false
    setCarregando(true)
    carregarTrafegoPago(filtro)
      .then((d) => {
        if (cancelado) return
        setDados(d)
        setErro(null)
      })
      .catch((e: Error) => {
        if (!cancelado) setErro(e.message)
      })
      .finally(() => {
        if (!cancelado) setCarregando(false)
      })
    return () => {
      cancelado = true
    }
  }, [filtro])

  const plataformas: Plataforma[] = plataforma ? [plataforma] : PLATAFORMAS.map((p) => p.id)

  // Um ponto por dia do intervalo (dia sem dado = zero), com o detalhe por plataforma.
  const dias = useMemo(() => {
    const porDia = new Map<string, { total: Metricas; porPlataforma: Partial<Record<Plataforma, Metricas>> }>()
    for (const d of diasDoIntervalo(intervalo)) porDia.set(d, { total: ZERO, porPlataforma: {} })
    for (const l of dados?.serie ?? []) {
      const dia = porDia.get(l.data)
      if (!dia) continue
      dia.total = somar(dia.total, l)
      dia.porPlataforma[l.plataforma] = somar(dia.porPlataforma[l.plataforma] ?? ZERO, l)
    }
    return [...porDia.entries()].map(([data, v]) => ({ data, ...v }))
  }, [dados, intervalo])

  const atual = useMemo(() => dias.reduce((acc, d) => somar(acc, d.total), ZERO), [dias])
  const anterior = dados?.anterior ?? ZERO
  const temDemo = Boolean(dados?.serie.some((l) => l.demo) || dados?.anterior.demo)
  const vazio = dados != null && dados.serie.length === 0

  // Nome da conversão: com uma marca, o nome que o Admin deu; com todas, genérico.
  const nomeConversao = marcaId ? (principais.find((p) => p.marcaId === marcaId)?.nome ?? 'Conversões') : 'Conversões'
  const marcasNoRecorte = marcaId ? marcas.filter((m) => m.id === marcaId) : marcas

  const pontos = (f: (m: Metricas) => number): PontoDiario[] =>
    dias.map((d) => ({
      data: d.data,
      ...Object.fromEntries(plataformas.map((p) => [p, f(d.porPlataforma[p] ?? ZERO)])),
    }))
  const serieDe = (f: (m: Metricas) => number | null) => dias.map((d) => f(d.total) ?? 0)

  const kpis = [
    {
      label: 'Investimento',
      valor: formatarReaisCompacto(atual.investimento),
      delta: variacao(atual.investimento, anterior.investimento),
      melhorQuando: 'neutro' as const,
      spark: serieDe((m) => m.investimento),
    },
    {
      label: 'Impressões',
      valor: formatarNumero(atual.impressoes),
      delta: variacao(atual.impressoes, anterior.impressoes),
      spark: serieDe((m) => m.impressoes),
    },
    {
      label: 'Cliques',
      valor: formatarNumero(atual.cliques),
      delta: variacao(atual.cliques, anterior.cliques),
      spark: serieDe((m) => m.cliques),
    },
    {
      label: 'CTR',
      valor: traco(razao(atual.cliques, atual.impressoes), formatarPercentual),
      delta: variacao(razao(atual.cliques, atual.impressoes), razao(anterior.cliques, anterior.impressoes)),
      spark: serieDe((m) => razao(m.cliques, m.impressoes)),
    },
    {
      label: 'CPC',
      valor: traco(razao(atual.investimento, atual.cliques), formatarReais),
      delta: variacao(razao(atual.investimento, atual.cliques), razao(anterior.investimento, anterior.cliques)),
      melhorQuando: 'desce' as const,
      spark: serieDe((m) => razao(m.investimento, m.cliques)),
    },
    {
      label: nomeConversao,
      valor: formatarConversoes(atual.conversoes),
      delta: variacao(atual.conversoes, anterior.conversoes),
      spark: serieDe((m) => m.conversoes),
      hint: marcaId ? undefined : 'Soma a conversão principal de cada marca',
    },
    {
      label: 'Custo por conversão',
      valor: traco(razao(atual.investimento, atual.conversoes), formatarReais),
      delta: variacao(razao(atual.investimento, atual.conversoes), razao(anterior.investimento, anterior.conversoes)),
      melhorQuando: 'desce' as const,
      spark: serieDe((m) => razao(m.investimento, m.conversoes)),
    },
    {
      label: 'ROAS',
      valor: atual.valorConversoes ? traco(razao(atual.valorConversoes, atual.investimento), formatarRoas) : '—',
      delta: variacao(razao(atual.valorConversoes, atual.investimento), razao(anterior.valorConversoes, anterior.investimento)),
      spark: serieDe((m) => razao(m.valorConversoes, m.investimento)),
      hint: atual.valorConversoes ? undefined : 'A conversão principal não registra valor (ex.: leads)',
    },
  ]

  const opcoesCampanha = dados?.campanhas ?? []

  return (
    <PainelShell titulo="Tráfego pago" fontes="Meta Ads · Google Ads">
      {/* Filtros do painel: uma linha só, acima de tudo o que eles recortam. */}
      <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap">
        <ToggleButtonGroup
          exclusive
          size="small"
          value={plataforma}
          onChange={(_, v: Plataforma | '' | null) => v !== null && setPlataforma(v)}
          aria-label="Plataforma"
        >
          <ToggleButton value="" sx={{ textTransform: 'none', px: 1.5 }}>
            Todas as plataformas
          </ToggleButton>
          {PLATAFORMAS.map((p) => (
            <ToggleButton key={p.id} value={p.id} sx={{ textTransform: 'none', px: 1.5 }}>
              {p.nome}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextField
          select
          size="small"
          label="Campanha"
          value={opcoesCampanha.some((c) => c.campanhaId === campanhaId) ? campanhaId : ''}
          onChange={(e) =>
            setCampanha(e.target.value ? { id: e.target.value, marcaId, plataforma } : null)
          }
          sx={{ minWidth: 260 }}
        >
          <MenuItem value="">Todas as campanhas</MenuItem>
          {opcoesCampanha.map((c) => (
            <MenuItem key={c.campanhaId} value={c.campanhaId}>
              {c.campanha}
              {marcaId ? '' : ` · ${c.marca}`}
              {plataforma ? '' : ` · ${nomePlataforma(c.plataforma)}`}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {temDemo ? (
        <Alert severity="warning" variant="outlined">
          <strong>Dados de demonstração.</strong> Números fictícios, só para testar o painel — saem quando os
          coletores estiverem ligados às contas reais.
        </Alert>
      ) : null}

      <AvisoConversao
        marcas={marcasNoRecorte}
        principais={principais}
        plataformasDaFonte={['meta_ads', 'google_ads']}
        nomeDaFonte="do Meta Ads ou do Google Ads"
      />

      {erro ? <Alert severity="error">Não foi possível carregar o painel: {erro}</Alert> : null}

      {vazio ? (
        <Paper variant="outlined" sx={{ pb: 6 }}>
          <ChartEmpty title="Sem dados de tráfego pago neste recorte" />
        </Paper>
      ) : (
        // Ao trocar filtro, a tela anterior fica esmaecida em vez de piscar.
        <Stack spacing={2} sx={{ opacity: carregando && dados ? 0.55 : 1, transition: 'opacity 160ms ease' }}>
          <LeituraIA painel="trafego_pago" filtros={{ plataforma, campanhaId }} />

          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
            }}
          >
            {kpis.map((k) => (
              <KpiCard
                key={k.label}
                label={k.label}
                value={k.valor}
                deltaPct={k.delta}
                deltaSufixo="vs período anterior"
                melhorQuando={k.melhorQuando}
                sparkline={k.spark}
                hint={k.hint}
                loading={!dados}
              />
            ))}
          </Box>

          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2" color="text.secondary">
              Por dia
            </Typography>
            <ToggleButtonGroup exclusive size="small" value={modo} onChange={(_, v) => v && setModo(v)} aria-label="Visualização">
              <ToggleButton value="grafico" sx={{ textTransform: 'none', px: 1.5 }}>
                Gráfico
              </ToggleButton>
              <ToggleButton value="tabela" sx={{ textTransform: 'none', px: 1.5 }}>
                Tabela
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {modo === 'grafico' ? (
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' } }}>
              <ColunasPorPlataforma
                titulo="Investimento"
                pontos={pontos((m) => m.investimento)}
                plataformas={plataformas}
                formatar={formatarReais}
                formatarEixo={formatarReaisEixo}
              />
              <ColunasPorPlataforma
                titulo={nomeConversao}
                pontos={pontos((m) => m.conversoes)}
                plataformas={plataformas}
                formatar={formatarConversoes}
                formatarEixo={formatarConversoes}
              />
            </Box>
          ) : (
            <TabelaDiaria
              colunas={plataformas.map((p) => ({ chave: p, nome: nomePlataforma(p) }))}
              series={[
                { titulo: 'Investimento', pontos: pontos((m) => m.investimento), formatar: formatarReais },
                { titulo: nomeConversao, pontos: pontos((m) => m.conversoes), formatar: formatarConversoes },
              ]}
            />
          )}

          <TabelaCampanhas
            campanhas={dados?.campanhas ?? []}
            mostrarMarca={!marcaId}
            selecionada={campanhaId}
            onSelecionar={(id) => setCampanha(id ? { id, marcaId, plataforma } : null)}
          />

          <DiagnosticoCusto plataforma={plataforma} campanhaId={campanhaId} />

          <DetalheAnuncios filtro={{ intervalo, marcaId, plataforma, campanhaId }} />

          <AnaliseCampanhas plataforma={plataforma} />
        </Stack>
      )}
    </PainelShell>
  )
}

function TabelaCampanhas({
  campanhas,
  mostrarMarca,
  selecionada,
  onSelecionar,
}: {
  campanhas: LinhaCampanha[]
  mostrarMarca: boolean
  selecionada: string
  onSelecionar: (id: string) => void
}) {
  const cores = useCoresPlataforma()
  const num = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const

  return (
    <Paper variant="outlined">
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ px: 2, pt: 2, pb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: '"Outfit", sans-serif' }}>
          Campanhas
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Clique numa campanha para filtrar o painel
        </Typography>
      </Stack>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Campanha</TableCell>
              <TableCell>Plataforma</TableCell>
              {mostrarMarca ? <TableCell>Marca</TableCell> : null}
              <TableCell align="right">Investimento</TableCell>
              <TableCell align="right">Cliques</TableCell>
              <TableCell align="right">Conversões</TableCell>
              <TableCell align="right">Custo por conversão</TableCell>
              <TableCell align="right">ROAS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campanhas.map((c) => {
              const ativa = c.campanhaId === selecionada
              return (
                <TableRow
                  key={c.campanhaId}
                  hover
                  selected={ativa}
                  onClick={() => onSelecionar(ativa ? '' : c.campanhaId)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{c.campanha}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: cores[c.plataforma] }} aria-hidden />
                      <span>{nomePlataforma(c.plataforma)}</span>
                    </Stack>
                  </TableCell>
                  {mostrarMarca ? <TableCell>{c.marca}</TableCell> : null}
                  <TableCell align="right" sx={num}>
                    {formatarReais(c.investimento)}
                  </TableCell>
                  <TableCell align="right" sx={num}>
                    {formatarInteiro(c.cliques)}
                  </TableCell>
                  <TableCell align="right" sx={num}>
                    {formatarConversoes(c.conversoes)}
                  </TableCell>
                  <TableCell align="right" sx={num}>
                    {traco(razao(c.investimento, c.conversoes), formatarReais)}
                  </TableCell>
                  <TableCell align="right" sx={num}>
                    {c.valorConversoes ? traco(razao(c.valorConversoes, c.investimento), formatarRoas) : '—'}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}
